import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Shield,
  AlertTriangle,
  Eye,
  Box,
  Layers,
  Timer,
  ChevronDown,
  ChevronUp,
  Lock,
  Fingerprint,
  Scale,
  Search
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// ReviewGate
// ─────────────────────────────────────────────────────────────
const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Execution approvata</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button onClick={() => onApprove()} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> Approva
          </button>
          <button onClick={onRegenerate} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> Rigenera
          </button>
          <button onClick={() => setShowRejectModal(true)} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Rigetta
          </button>
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Spiega perche rigetti questa execution..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4} />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Annulla
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ConstraintSection
// ─────────────────────────────────────────────────────────────
const ConstraintSection = ({ title, icon: Icon, items = [], colorClass = 'text-gray-700', bgClass = 'bg-gray-50' }) => {
  const [expanded, setExpanded] = useState(true);
  if (!items || !items.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${colorClass}`} />
          <span className="font-medium text-gray-800">{title}</span>
          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">{items.length}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className={`${bgClass} p-4`}>
          <ul className="space-y-2">
            {items.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-white border border-gray-200 text-xs text-gray-500 mt-0.5">{i + 1}</span>
                <span className="text-sm text-gray-700">{typeof item === 'string' ? item : JSON.stringify(item)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// EmptyState
// ─────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center text-gray-500">
      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
      <p className="text-sm">Clicca "Esegui Contract" per avviare il Creative Constraints Engine</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Contract02View
// ─────────────────────────────────────────────────────────────
const Contract02View = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  if (!execution) return <EmptyState />;

  const output = execution.output || execution.outputData || {};
  const cc = output.creative_constraints || {};

  const hasHigherOrder = (cc.higher_order_robustness_constraints?.length > 0)
    || (cc.artificial_orchestration_limits?.length > 0)
    || (cc.autonomy_preservation_constraints?.length > 0)
    || (cc.evidence_independence_constraints?.length > 0);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Shield className="w-6 h-6 text-cyan-600" />
            <h2 className="text-xl font-bold text-gray-800">Creative Constraints Engine</h2>
            <span className="px-2 py-0.5 text-xs bg-cyan-100 text-cyan-700 rounded-full font-medium">C02</span>
          </div>
          <p className="text-sm text-gray-500">Framework di vincoli creativi e produttivi per questo progetto</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: 'Constraints', count: (cc.constraints || []).length, color: 'text-blue-600' },
            { label: 'Forbidden', count: (cc.forbidden_patterns || []).length, color: 'text-red-600' },
            { label: 'Rendering', count: (cc.rendering_limits || []).length, color: 'text-orange-600' },
            { label: 'Realism', count: (cc.realism_requirements || []).length, color: 'text-purple-600' },
            { label: 'Ambiguity', count: (cc.ambiguity_limits || []).length, color: 'text-gray-600' }
          ].map(({ label, count, color }) => (
            <div key={label} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <div className={`text-2xl font-bold ${color}`}>{count}</div>
              <div className="text-xs text-gray-500 mt-1">{label}</div>
            </div>
          ))}
        </div>

        {/* Higher-Order stats (shown only when present) */}
        {hasHigherOrder && (
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'HO Robustness', count: (cc.higher_order_robustness_constraints || []).length, color: 'text-violet-600' },
              { label: 'Orchestration', count: (cc.artificial_orchestration_limits || []).length, color: 'text-indigo-600' },
              { label: 'Autonomy', count: (cc.autonomy_preservation_constraints || []).length, color: 'text-teal-600' },
              { label: 'Evidence', count: (cc.evidence_independence_constraints || []).length, color: 'text-emerald-600' }
            ].map(({ label, count, color }) => (
              <div key={label} className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-center">
                <div className={`text-2xl font-bold ${color}`}>{count}</div>
                <div className="text-xs text-gray-500 mt-1">{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Standard Constraint Sections */}
        <div className="space-y-3">
          <ConstraintSection
            title="Constraints"
            icon={Layers}
            items={cc.constraints}
            colorClass="text-blue-600"
            bgClass="bg-blue-50"
          />
          <ConstraintSection
            title="Forbidden Patterns"
            icon={XCircle}
            items={cc.forbidden_patterns}
            colorClass="text-red-600"
            bgClass="bg-red-50"
          />
          <ConstraintSection
            title="Rendering Limits"
            icon={Box}
            items={cc.rendering_limits}
            colorClass="text-orange-600"
            bgClass="bg-orange-50"
          />
          <ConstraintSection
            title="Realism Requirements"
            icon={Eye}
            items={cc.realism_requirements}
            colorClass="text-purple-600"
            bgClass="bg-purple-50"
          />
          <ConstraintSection
            title="Ambiguity Limits"
            icon={AlertTriangle}
            items={cc.ambiguity_limits}
            colorClass="text-yellow-600"
            bgClass="bg-yellow-50"
          />
        </div>

        {/* Higher-Order Constraint Sections */}
        {hasHigherOrder && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pt-2">
              <Shield className="w-4 h-4 text-violet-600" />
              <h3 className="text-sm font-semibold text-violet-700 uppercase tracking-wide">Higher-Order Constraints</h3>
            </div>
            <ConstraintSection
              title="Higher-Order Robustness Constraints"
              icon={Lock}
              items={cc.higher_order_robustness_constraints}
              colorClass="text-violet-600"
              bgClass="bg-violet-50"
            />
            <ConstraintSection
              title="Artificial Orchestration Limits"
              icon={Fingerprint}
              items={cc.artificial_orchestration_limits}
              colorClass="text-indigo-600"
              bgClass="bg-indigo-50"
            />
            <ConstraintSection
              title="Autonomy Preservation Constraints"
              icon={Scale}
              items={cc.autonomy_preservation_constraints}
              colorClass="text-teal-600"
              bgClass="bg-teal-50"
            />
            <ConstraintSection
              title="Evidence Independence Constraints"
              icon={Search}
              items={cc.evidence_independence_constraints}
              colorClass="text-emerald-600"
              bgClass="bg-emerald-50"
            />
          </div>
        )}

        {/* Duration Constraints */}
        {cc.duration_constraints && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Timer className="w-5 h-5 text-cyan-600" />
              <h3 className="font-medium text-gray-800">Duration Constraints</h3>
            </div>
            <div className="flex gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Target</p>
                <p className="text-lg font-semibold text-cyan-700">
                  {cc.duration_constraints.target_duration_seconds != null
                    ? `${cc.duration_constraints.target_duration_seconds}s`
                    : 'non specificato'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Maximum</p>
                <p className="text-lg font-semibold text-gray-700">
                  {cc.duration_constraints.maximum_duration_seconds != null
                    ? `${cc.duration_constraints.maximum_duration_seconds}s`
                    : 'non specificato'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Review Gate */}
        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default Contract02View;
